// src/components/LoginScreen.js
import React, { useState, useRef } from 'react';
import appIcon from '/icons/icon-512.png';
import { useAuth } from '@/contexts/AuthContext';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import PurposeModal from '@/components/shared/PurposeModal';

export default function LoginScreen() {
  const { login, register, authError, setAuthError } = useAuth();

  // 'purpose' | 'welcome' | 'login' | 'register'
  const [step,     setStep]     = useState('purpose');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [name,     setName]     = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [showCf,   setShowCf]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [localErr, setLocalErr] = useState('');

  const emailRef    = useRef(null);
  const passwordRef = useRef(null);

  const clearErrors = () => { setLocalErr(''); setAuthError(null); };

  const goTo = (s) => { setStep(s); clearErrors(); setTimeout(() => emailRef.current?.focus(), 100); };
  const goBack = () => { setStep('welcome'); setPassword(''); setConfirm(''); clearErrors(); };

  const handleSubmit = async () => {
    clearErrors();
    const resolvedEmail    = emailRef.current?.value?.trim() || email.trim();
    const resolvedPassword = passwordRef.current?.value      || password;

    if (step === 'login') {
      if (!resolvedEmail)    return setLocalErr('Please enter your email.');
      if (!resolvedPassword) return setLocalErr('Please enter your password.');
      setLoading(true);
      const result = await login(resolvedEmail, resolvedPassword);
      setLoading(false);
      if (!result.success) setLocalErr(result.error || 'Sign in failed.');

    } else if (step === 'register') {
      if (!resolvedEmail)                        return setLocalErr('Please enter your email.');
      if (!/\S+@\S+\.\S+/.test(resolvedEmail))   return setLocalErr('Please enter a valid email address.');
      if (!resolvedPassword)                     return setLocalErr('Please create a password.');
      if (resolvedPassword.length < 6)           return setLocalErr('Password must be at least 6 characters.');
      if (resolvedPassword !== confirm)          return setLocalErr('Passwords do not match.');
      setLoading(true);
      const result = await register(resolvedEmail, resolvedPassword, name.trim() || undefined);
      setLoading(false);
      if (!result.success) setLocalErr(result.error || 'Registration failed.');
    }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSubmit(); };

  // ── Shared styles ──────────────────────────────────────────────
  const inputBase = {
    width: '100%', padding: '12px 14px', borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.07)', color: 'white',
    fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const pwWrap  = { position: 'relative', width: '100%' };
  const pwInput = { ...inputBase, paddingRight: '44px' };
  const eyeBtn  = {
    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer',
    color: 'rgba(255,255,255,0.5)', padding: '4px', display: 'flex', alignItems: 'center',
  };
  const primaryBtn = {
    width: '100%', padding: '13px', borderRadius: '10px', border: 'none',
    background: 'linear-gradient(135deg, #4d7c0f, #166534)',
    color: 'white', fontSize: '1rem', fontWeight: '700',
    cursor: loading ? 'not-allowed' : 'pointer',
    opacity: loading ? 0.7 : 1, letterSpacing: '0.02em', fontFamily: 'inherit',
  };
  const outlineBtn = {
    width: '100%', padding: '13px', borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.2)',
    background: 'rgba(255,255,255,0.07)', color: 'white',
    fontSize: '1rem', fontWeight: '700', cursor: 'pointer',
    letterSpacing: '0.02em', fontFamily: 'inherit',
  };
  const ghostBtn = {
    background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)',
    fontSize: '0.82rem', cursor: 'pointer', padding: '4px 0',
    fontFamily: 'inherit', textDecoration: 'underline',
  };
  const srOnly = {
    position: 'absolute', width: '1px', height: '1px', padding: 0,
    margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap', borderWidth: 0,
  };

  const error = localErr || authError;

  // ── Reusable eye toggle ────────────────────────────────────────
  const EyeToggle = ({ show, onToggle, label }) => (
    <button style={eyeBtn} onClick={onToggle} tabIndex={-1} type="button" aria-label={label}>
      {show ? <EyeOff size={18} /> : <Eye size={18} />}
    </button>
  );

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '100vh', padding: '24px',
      background: 'linear-gradient(160deg, #14532d 0%, #1c1917 60%, #292524 100%)',
    }}>
      {/* Purpose modal — shown as overlay on first visit */}
      {step === 'purpose' && (
        <PurposeModal onClose={() => setStep('welcome')} closeLabel="Get Started →" />
      )}

      {/* Logo */}
      <img src={appIcon} alt="Sheepheard" style={{
        width: '72px', height: '72px', borderRadius: '18px', objectFit: 'contain',
        marginBottom: '12px', boxShadow: '0 8px 24px rgba(77,124,15,0.35)',
      }} />
      <p style={{ fontFamily: 'Georgia, serif', fontWeight: '800', fontSize: '1.4rem', color: 'white', margin: '0 0 4px' }}>
        Sheepheard
      </p>
      <p style={{
        color: 'rgba(255,255,255,0.55)', fontSize: '0.78rem', fontStyle: 'italic',
        fontWeight: '400', marginBottom: '28px', textAlign: 'center',
        maxWidth: '300px', lineHeight: '1.5',
      }}>
        "My sheep hear my voice, and I know them, and they follow me." — John 10:27
      </p>

      {/* Card */}
      <div style={{
        width: '100%', maxWidth: '380px',
        background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '16px', padding: '28px 24px',
        display: 'flex', flexDirection: 'column', gap: '14px',
      }}>

        {/* ── Welcome ── */}
        {step === 'welcome' && (
          <>
            <div style={{ marginBottom: '4px' }}>
              <p style={{ color: 'white', fontWeight: '700', fontSize: '1.05rem', margin: '0 0 6px' }}>Welcome</p>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', margin: 0 }}>
                Sign in to your account or create a new one to get started.
              </p>
            </div>
            <button onClick={() => goTo('login')} style={primaryBtn}>Sign In</button>
            <button onClick={() => goTo('register')} style={outlineBtn}>Create Account</button>
            <p style={{ textAlign: 'center', margin: 0 }}>
              <button onClick={() => setStep('purpose')} style={{ ...ghostBtn, fontSize: '0.75rem' }}>
                About Sheepheard
              </button>
            </p>
          </>
        )}

        {/* ── Sign In ── */}
        {step === 'login' && (
          <>
            <div style={{ marginBottom: '2px' }}>
              <p style={{ color: 'white', fontWeight: '700', fontSize: '1.05rem', margin: '0 0 4px' }}>Welcome back</p>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', margin: 0 }}>Sign in to continue</p>
            </div>

            <label htmlFor="login-email" style={srOnly}>Email address</label>
            <input id="login-email" name="login-email" ref={emailRef} type="email"
              placeholder="Email address" value={email} autoComplete="email" autoFocus
              onChange={e => { setEmail(e.target.value); clearErrors(); }}
              onKeyDown={handleKeyDown} style={inputBase} />

            <div style={pwWrap}>
              <label htmlFor="login-password" style={srOnly}>Password</label>
              <input id="login-password" name="login-password" ref={passwordRef}
                type={showPw ? 'text' : 'password'} placeholder="Password" value={password}
                autoComplete="current-password"
                onChange={e => { setPassword(e.target.value); clearErrors(); }}
                onKeyDown={handleKeyDown} style={pwInput} />
              <EyeToggle show={showPw} onToggle={() => setShowPw(v => !v)}
                label={showPw ? 'Hide password' : 'Show password'} />
            </div>

            {error && <p style={{ color: '#fca5a5', fontSize: '0.83rem', textAlign: 'center', margin: 0 }} role="alert">{error}</p>}

            <button onClick={handleSubmit} disabled={loading} style={primaryBtn}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
            <p style={{ textAlign: 'center', margin: 0, color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>
              No account yet?{' '}<button onClick={() => goTo('register')} style={ghostBtn}>Create one</button>
            </p>
            <p style={{ textAlign: 'center', margin: 0 }}>
              <button onClick={goBack} style={{ ...ghostBtn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <ArrowLeft size={13} /> Back
              </button>
            </p>
          </>
        )}

        {/* ── Register ── */}
        {step === 'register' && (
          <>
            <div style={{ marginBottom: '2px' }}>
              <p style={{ color: 'white', fontWeight: '700', fontSize: '1.05rem', margin: '0 0 4px' }}>Create your account</p>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', margin: 0 }}>Join Sheepheard and start your journey</p>
            </div>

            <label htmlFor="register-email" style={srOnly}>Email address</label>
            <input id="register-email" name="register-email" ref={emailRef} type="email"
              placeholder="Email address" value={email} autoComplete="email" autoFocus
              onChange={e => { setEmail(e.target.value); clearErrors(); }}
              onKeyDown={handleKeyDown} style={inputBase} />

            <label htmlFor="register-name" style={srOnly}>Your name (optional)</label>
            <input id="register-name" name="register-name" type="text"
              placeholder="Your name (optional)" value={name} autoComplete="name"
              onChange={e => { setName(e.target.value); clearErrors(); }} style={inputBase} />

            <div style={pwWrap}>
              <label htmlFor="register-password" style={srOnly}>Create password (min 6 characters)</label>
              <input id="register-password" name="register-password" ref={passwordRef}
                type={showPw ? 'text' : 'password'} placeholder="Create password (min 6 chars)"
                value={password} autoComplete="new-password"
                onChange={e => { setPassword(e.target.value); clearErrors(); }}
                onKeyDown={handleKeyDown} style={pwInput} />
              <EyeToggle show={showPw} onToggle={() => setShowPw(v => !v)}
                label={showPw ? 'Hide password' : 'Show password'} />
            </div>

            <div style={pwWrap}>
              <label htmlFor="register-confirm" style={srOnly}>Confirm password</label>
              <input id="register-confirm" name="register-confirm"
                type={showCf ? 'text' : 'password'} placeholder="Confirm password"
                value={confirm} autoComplete="new-password"
                onChange={e => { setConfirm(e.target.value); clearErrors(); }}
                onKeyDown={handleKeyDown} style={pwInput} />
              <EyeToggle show={showCf} onToggle={() => setShowCf(v => !v)}
                label={showCf ? 'Hide confirm password' : 'Show confirm password'} />
            </div>

            {error && <p style={{ color: '#fca5a5', fontSize: '0.83rem', textAlign: 'center', margin: 0 }} role="alert">{error}</p>}

            <button onClick={handleSubmit} disabled={loading} style={primaryBtn}>
              {loading ? 'Creating account…' : 'Create Account'}
            </button>
            <p style={{ textAlign: 'center', margin: 0, color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem' }}>
              Already have an account?{' '}<button onClick={() => goTo('login')} style={ghostBtn}>Sign in</button>
            </p>
            <p style={{ textAlign: 'center', margin: 0 }}>
              <button onClick={goBack} style={{ ...ghostBtn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <ArrowLeft size={13} /> Back
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}